import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { Component } from "schemas/component";
import type { ReploElement } from "schemas/generated/element";

import {
  findAncestorComponent,
  findAncestorComponentOrSelf,
} from "@v1/utils/component";
import { getSharedStateKey } from "replo-runtime/shared/state";
import {
  findComponentByTypeDepthFirst,
  getChildren,
} from "replo-runtime/shared/utils/component";
import { isItemsDynamic } from "replo-runtime/store/utils/items";
import { isNotNullish, isNullish } from "replo-utils/lib/misc";

/**
 * Return the index of the nearest ancestor carouselV3 slide to the given
 * component id, or null if the component is not inside a carouselV3 slide.
 */
export const nearestCarouselSlideIndex = (
  _canvas: EditorCanvas,
  componentId: string | null,
  element: ReploElement | null,
) => {
  const slidesComponent = findAncestorComponent(
    element,
    componentId,
    (component) => component.type === "carouselV3Slides",
  );
  if (
    !componentId ||
    !slidesComponent ||
    !element ||
    !slidesComponent?.children?.[0]?.id
  ) {
    return { index: null };
  }

  const slideComponent = findAncestorComponentOrSelf(
    element,
    componentId,
    (_, parent) => parent?.type === "carouselV3Slides",
  );

  const index = slidesComponent?.children?.findIndex(
    (child) => child.id === slideComponent?.id,
  );

  return { index };
};

/**
 * Return whether selecting the given component would constitute a transition from
 * one slide to another of the component's nearest carousel. This is useful for when
 * we want to automatically transition a carousel to the slide that contains the
 * component the user has selected.
 */
export const isSlideTransition = (
  newSelectedComponentId: string | null,
  element: ReploElement | null,
  sharedState: Record<string, any>,
) => {
  // Note: Fran (2022-05-11) if the selected id is a child of a carousel we
  // need to set the new active slide to show it in the editor
  const parentCarouselComponent = findAncestorComponent(
    element,
    newSelectedComponentId,
    (component) => component.type === "carouselV3",
  );
  const slidesComponent = findAncestorComponentOrSelf(
    element,
    newSelectedComponentId,
    (component) => component.type === "carouselV3Slides",
  );
  const slideComponent = findAncestorComponentOrSelf(
    element,
    newSelectedComponentId,
    (_, parent) => parent?.type === "carouselV3Slides",
  );
  const slideIndex = slidesComponent?.children?.findIndex(
    (child) => child.id === slideComponent?.id,
  );
  if (
    parentCarouselComponent &&
    isNotNullish(slideIndex) &&
    slideIndex !== -1
  ) {
    const activeSlideStateKey = getSharedStateKey(
      { id: parentCarouselComponent.id, type: "carouselV3" },
      "activeSlide",
    );
    const activeSlideFromState = sharedState[activeSlideStateKey];
    if (isNullish(activeSlideFromState)) {
      return slideIndex !== 0;
    }
    if (slideIndex !== sharedState[activeSlideStateKey]) {
      return true;
    }
  }
  return false;
};

export const getComponentIdFromSlideIndex = (
  _canvas: EditorCanvas,
  component: Component,
  slideIndex: number,
) => {
  const slideListComponent = findComponentByTypeDepthFirst(
    component,
    "carouselV3Slides",
  );

  const carouselHasDynamicData = isItemsDynamic(component?.props._items);

  if (!slideListComponent) {
    return;
  }

  const slideChildren = getChildren(slideListComponent);

  const componentId = carouselHasDynamicData
    ? slideChildren[0]?.id
    : slideChildren[slideIndex]?.id;

  return {
    componentId,
  };
};
